import { Rectangle, Sprite, Texture } from 'pixi.js';
import { Element } from './Element.class';
import { getScaleWH } from '@/utils/canvasUtil';
import { textureManager } from '@/managers/TextureManager';
import { PixiGlobal } from '../PixiGlobal';
import type { ElementLoadOptions } from '@/types/canvas-type';
import { classifyImageLoadFailure } from './imageLoadFailure';

export class ImageElement extends Element {
  private sprite: Sprite | null = null;
  private _loadedUrl: string | null = null;
  /** 原始完整纹理，crop='none' 时直接复用 */
  private _sourceTexture: Texture | null = null;

  // ============================================================
  // 公共生命周期
  // ============================================================

  public override async load(options?: ElementLoadOptions): Promise<boolean> {
    if (typeof this._data.content !== 'string' || !this._data.content.trim()) {
      options?.onImageLoadFailure?.('permanent');
      return false;
    }

    const url = this._data.content;
    let texture: Texture | null = null;
    try {
      texture = await this._loadTexture(url, options);
      this._applyTexture(texture, url);
      return true;
    } catch (error) {
      if (texture) textureManager.release(url);
      if (error instanceof DOMException && error.name === 'AbortError') return false;
      const failureKind = await classifyImageLoadFailure(url, error, {
        signal: options?.signal
      });
      if (failureKind) options?.onImageLoadFailure?.(failureKind);
      console.error(`ImageElement加载失败: url:${url} - ${error}`);
      return false;
    }
  }

  /**
   * 重载：加载新纹理 → swap 到现有 sprite → 卸载旧纹理 → 更新尺寸。
   * 不销毁/重建 sprite，避免 PixiJS 内部状态异常。
   */
  public override async reload(): Promise<boolean> {
    if (typeof this._data.content !== 'string') return false;

    const newUrl = this._data.content;
    let newTexture: Texture | null = null;

    try {
      newTexture = await this._loadTexture(newUrl);
      newTexture.source.scaleMode = 'linear';

      if (this.sprite && !this.sprite.destroyed) {
        this.sprite.texture = newTexture;
      } else {
        this.sprite = new Sprite(newTexture);
        this.sprite.anchor.set(0.5);
        this.setTarget(this.sprite);
      }

      // 先 acquire 新 URL 再 release 旧 URL：同 URL 重载时计数不会中途归零误销毁在用 source
      const previousUrl = this._loadedUrl;
      this._updateAttrs(newTexture, newUrl);
      if (previousUrl) textureManager.release(previousUrl);

      return true;
    } catch (error) {
      if (newTexture) textureManager.release(newUrl);
      console.error(`ImageElement重载失败: url:${newUrl} - ${error}`);
      return false;
    }
  }

  public override unload(): void {
    this._unloadOldTexture();
  }

  // ============================================================
  // 公共方法
  // ============================================================

  /** 动态切换裁剪模式 */
  public updateCrop(crop?: 'left' | 'right' | 'none'): void {
    if (!this._data.config) this._data.config = {};
    this._data.config.crop = crop;
    if (!this.sprite || this.sprite.destroyed) return;
    this._applyCrop();
    this._refreshSize();
  }

  // ============================================================
  // 内部方法
  // ============================================================

  /** 加载纹理：blob URL 无扩展名，需显式指定 parser */
  private async _loadTexture(url: string, options?: ElementLoadOptions): Promise<Texture> {
    return textureManager.loadTexture(url, {
      parser: url.startsWith('blob:') ? 'texture' : undefined,
      signal: options?.signal,
      timeoutMs: options?.timeoutMs
    });
  }

  /** 首次创建 sprite + 应用纹理 + 更新尺寸 */
  private _applyTexture(texture: Texture, url: string): void {
    this.sprite = new Sprite(texture);
    this.sprite.anchor.set(0.5);
    this.setTarget(this.sprite);
    this._updateAttrs(texture, url);
  }

  /** 保存原始纹理并应用裁剪 + 尺寸 */
  private _updateAttrs(texture: Texture, url: string): void {
    this._sourceTexture = texture;
    texture.source.scaleMode = 'linear';
    this._enableMipmaps(texture);
    this._applyCrop();
    this._refreshSize();
    this._loadedUrl = url;
  }

  /**
   * 从 Assets 缓存中移除旧纹理引用，不销毁底层 TextureSource。
   *
   * 不能直接调用 Assets.unload()：它内部会 destroy(true) 连带销毁 TextureSource。
   * 如果该 TextureSource 被其他页面/元素的 Sprite 共享（如同一个 CDN 图片 URL），
   * 渲染时会因 source.style 为 null 而抛出 "Cannot read properties of null
   * (reading 'addressModeU')"。
   *
   * 这里按 URL 引用计数 release：计数归零才 destroy(true) 释放 TextureSource，
   * 避免共享 source 被提前销毁导致渲染崩溃，同时不再让显存随阅读单调上涨。
   */
  private _unloadOldTexture(): void {
    if (!this._loadedUrl) return;
    textureManager.release(this._loadedUrl);
    this._loadedUrl = null;
  }

  /** 根据 attrs.crop 裁剪纹理并赋给 sprite */
  private _applyCrop(): void {
    if (!this.sprite || this.sprite.destroyed || !this._sourceTexture) return;

    const crop = this._data.config?.crop;

    if (!crop || crop === 'none') {
      this.sprite.texture = this._sourceTexture;
      return;
    }

    const sw = this._sourceTexture.width;
    const sh = this._sourceTexture.height;

    const frame =
      crop === 'left' ? new Rectangle(0, 0, sw / 2, sh) : new Rectangle(sw / 2, 0, sw / 2, sh);

    const croppedTexture = new Texture({
      source: this._sourceTexture.source,
      frame
    });
    croppedTexture.source.scaleMode = 'linear';

    this.sprite.texture = croppedTexture;
  }

  /**
   * 为纹理源启用 GPU mipmap，消除缩放时的锯齿（走样）。
   *
   * GL_LINEAR 双线性过滤在纹理被缩小超过约 1.5x 时锯齿明显。
   * 启用 autoGenerateMipmaps + 设置 mipLevelCount 后，
   * GlTextureSystem.updateStyle 会将 minFilter 改为
   * LINEAR_MIPMAP_LINEAR（三线性过滤），GPU 从预生成的
   * 缩小版纹理中采样，高对比度边缘（如人物黑色轮廓线）也能平滑。
   *
   * 仅当渲染器就绪、纹理尺寸 ≥ 2 时才启用。
   */
  private _enableMipmaps(texture: Texture): void {
    const source = texture.source;
    const maxDim = Math.max(source.width, source.height);

    // 尺寸为 1 的纹理（极端情况）无法生成 mipmap
    if (maxDim <= 1) return;

    const mipLevelCount = Math.floor(Math.log2(maxDim)) + 1;
    if (mipLevelCount <= 1) return;

    source.autoGenerateMipmaps = true;
    source.mipLevelCount = mipLevelCount;

    // 仅在渲染器已初始化、纹理已上传到 GPU 时才触发 mipmap 生成
    if (PixiGlobal.app?.renderer) {
      // 触发 gl.generateMipmap()
      source.updateMipmaps();
      // 触发 styleChange → GlTextureSystem.updateStyle → TEXTURE_MIN_FILTER = LINEAR_MIPMAP_LINEAR
      source.style.update();
    }
  }

  /** 根据 sprite 当前纹理尺寸刷新 attrs 并应用到视图 */
  private _refreshSize(): void {
    const texture = this.sprite?.texture;
    if (!texture) return;

    const attrs = this.attrs;
    const isFull = this.data.config?.fullscreen || false;
    let { width: rectW = 300, height: rectH = 300 } = attrs;

    if (this.attrs.layout?.parentWidth) {
      rectW = this.attrs.layout.parentWidth * (this.attrs.layout.width || 1);
    }

    if (this.attrs.layout?.parentHeight) {
      rectH = this.attrs.layout.parentHeight * (this.attrs.layout.height || 1);
    }

    const { width, height } = getScaleWH(rectW, rectH, texture.width / texture.height, isFull);

    const ds = this.attrs.layout?.displayScale ?? 1;
    Object.assign(attrs, {
      width: width / ds,
      height: height / ds,
      originWidth: texture.width,
      originHeight: texture.height
    });

    this.applyAttrs();
  }

  public override destroy(): void {
    // 阅读端卸页经 action.destroy → element.destroy 而非 unload，需在此 release 释放显存
    this._unloadOldTexture();
    this._sourceTexture = null;
    if (this.sprite && !this.sprite.destroyed) {
      this.sprite.destroy({ children: true, texture: false, textureSource: false });
    }
    super.destroy();
  }
}
